import { ALGORITHMS } from '../VerticalCards/VerticalCardsContainer';
import { selectVertical } from '../Components/Filter/FiltersSlice';
import store from '../../store';

/**
 * Which verticals the viewer offers a radiology report on.
 *
 * The answer comes from `filters.vertical`, the vertical the user picked in the
 * study list, and not from the `Algorithm` field of the series on screen. The two
 * are not the same question: a study can carry series from several algorithms, and
 * the acquisition series the viewer opens by default carries no schema at all (see
 * reportSeries.js), so the series can be silent exactly when the toolbar is being
 * built. The picked vertical is never silent and never ambiguous — it is what the
 * user asked to look at.
 *
 * The known limit: `filters.vertical` is not persisted. store/index.js:59-68 only
 * mirrors `preferences`, `feedback.lastFeedbackDate` and `servers`, so after an F5
 * or on a shared viewer URL the state is back to null and the report reappears on
 * an AZNOD study. Accepted rather than worked around, and not a new behaviour: the
 * viewer's accent colour is already driven by the very same state with the very same
 * consequence (AZmed/Components/Title/Title.js:37-46). Making this one survive a
 * reload would mean persisting the vertical, which is a decision of its own.
 *
 * This module reads the store, which the rest of AZmed/Data/ deliberately does not:
 * that much purity is given up here on purpose. The alternative was to let each
 * caller — the toolbar button and the panel opened on mount — compose the rule out
 * of the pure predicate itself, and two compositions of one rule drift apart in
 * silence. What that drift produces is the panel opened on mount with no button left
 * to close it, and no way out for the user. The pure predicate stays exported right
 * next to the composed one, so nothing testable is lost.
 */

/**
 * Whether the vertical is one the report panel must stay hidden on.
 *
 * An absent vertical answers false — show the report. That is the safe default
 * here: hiding on a doubt takes a document away from a radiologist who has no way to
 * ask for it back, while showing it on a vertical that has none only costs an empty
 * panel the user can close.
 *
 * @param {Object} [vertical] The picked vertical, a `filters.vertical` entry
 * @returns {boolean}
 */
export const isReportlessVertical = vertical =>
  Boolean(vertical) && vertical.name === ALGORITHMS.AZNODE;

/**
 * The same question asked of the current state, for the callers that have no
 * vertical in hand. The single place the store is read for this rule, so the toolbar
 * button and the panel opened on mount cannot end up answering differently.
 * @returns {boolean}
 */
export const isReportHiddenHere = () =>
  isReportlessVertical(selectVertical(store.getState()));
